import { useState, useEffect } from 'react';
import { Plus, Trash2, Play, Loader as Loader2, ArrowDown, ChevronDown, ChevronUp, GripVertical } from 'lucide-react';
import type { CompanySkill, WorkflowDefinition } from './types';
import { SCHEDULE_LABELS } from './types';
import type { ScheduleType } from './types';
import { supabase } from '@/lib/supabase';
import { useCompany } from '@/context/CompanyContext';
import { useAuth } from '@/features/auth';

interface Props {
  activeSkills: CompanySkill[];
  onRunWorkflow: (workflowId: string) => void;
  runningId: string | null;
}

export function WorkflowBuilder({ activeSkills, onRunWorkflow, runningId }: Props) {
  const { activeCompanyId } = useCompany();
  const { user } = useAuth();

  const [workflows, setWorkflows] = useState<WorkflowDefinition[]>([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  // New workflow form
  const [showForm, setShowForm] = useState(false);
  const [newName, setNewName] = useState('');
  const [newDesc, setNewDesc] = useState('');
  const [newSchedule, setNewSchedule] = useState<ScheduleType>('manual');
  const [selectedSkillIds, setSelectedSkillIds] = useState<string[]>([]);

  useEffect(() => {
    loadWorkflows();
  }, [activeCompanyId]);

  const loadWorkflows = async () => {
    if (!activeCompanyId) return;
    setLoading(true);
    const { data } = await supabase
      .from('workflow_definitions')
      .select(`*, workflow_steps(*, company_skills(name_override, skill_definitions(name_he)))`)
      .eq('company_id', activeCompanyId)
      .order('created_at');
    setWorkflows((data as WorkflowDefinition[]) ?? []);
    setLoading(false);
  };

  const handleCreate = async () => {
    if (!newName.trim() || selectedSkillIds.length === 0 || !activeCompanyId || !user) return;
    setCreating(true);
    try {
      const { data: wf, error } = await supabase
        .from('workflow_definitions')
        .insert({
          company_id: activeCompanyId,
          user_id: user.id,
          name: newName.trim(),
          description: newDesc.trim() || null,
          schedule_type: newSchedule,
          is_active: true,
        })
        .select('id')
        .single();
      if (error || !wf) throw error;

      const steps = selectedSkillIds.map((csId, i) => ({
        workflow_id: wf.id,
        company_skill_id: csId,
        step_order: i + 1,
      }));
      await supabase.from('workflow_steps').insert(steps);

      setNewName('');
      setNewDesc('');
      setSelectedSkillIds([]);
      setShowForm(false);
      await loadWorkflows();
    } catch (e) {
      console.error(e);
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (id: string) => {
    await supabase.from('workflow_definitions').delete().eq('id', id);
    setWorkflows(prev => prev.filter(w => w.id !== id));
  };

  const toggleSkill = (csId: string) => {
    setSelectedSkillIds(prev =>
      prev.includes(csId) ? prev.filter(id => id !== csId) : [...prev, csId]
    );
  };

  if (loading) return (
    <div className="flex items-center justify-center py-16">
      <Loader2 className="w-6 h-6 animate-spin text-blue-500" />
    </div>
  );

  return (
    <div className="space-y-6" dir="rtl">

      {/* Header + new button */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-bold text-gray-900">זרימות עבודה</h2>
          <p className="text-sm text-gray-500 mt-0.5">שרשר סקילים לתהליך עבודה אוטומטי</p>
        </div>
        <button onClick={() => setShowForm(!showForm)}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-xl hover:bg-blue-700 transition-colors">
          <Plus className="w-4 h-4" />
          זרימה חדשה
        </button>
      </div>

      {/* Create form */}
      {showForm && (
        <div className="bg-white border border-blue-200 rounded-2xl p-5 shadow-sm space-y-4">
          <h3 className="text-sm font-bold text-gray-800">יצירת זרימת עבודה חדשה</h3>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-gray-500 mb-1">שם הזרימה *</label>
              <input value={newName} onChange={e => setNewName(e.target.value)} placeholder="לדוגמה: תהליך שכר חודשי"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-500 mb-1">תדירות</label>
              <select value={newSchedule} onChange={e => setNewSchedule(e.target.value as ScheduleType)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                {(Object.keys(SCHEDULE_LABELS) as ScheduleType[]).map(t => (
                  <option key={t} value={t}>{SCHEDULE_LABELS[t]}</option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-500 mb-1">תיאור (אופציונלי)</label>
            <input value={newDesc} onChange={e => setNewDesc(e.target.value)} placeholder="תיאור קצר..."
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-500 mb-2">סקילים פעילים (בחר לפי סדר ריצה)</label>
            {activeSkills.length === 0 ? (
              <p className="text-sm text-gray-400">אין סקילים פעילים. הפעל סקילים קודם מהחנות.</p>
            ) : (
              <div className="space-y-2">
                {activeSkills.map(cs => {
                  const idx = selectedSkillIds.indexOf(cs.id);
                  const selected = idx !== -1;
                  return (
                    <button key={cs.id} onClick={() => toggleSkill(cs.id)}
                      className={`w-full flex items-center gap-3 px-4 py-2.5 rounded-xl border text-sm text-right transition-all
                        ${selected ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-gray-300'}`}>
                      <GripVertical className="w-4 h-4 text-gray-300 shrink-0" />
                      {selected && (
                        <span className="w-5 h-5 rounded-full bg-blue-600 text-white text-xs font-bold flex items-center justify-center shrink-0">
                          {idx + 1}
                        </span>
                      )}
                      <span className={`flex-1 font-medium ${selected ? 'text-blue-800' : 'text-gray-700'}`}>
                        {cs.name_override ?? cs.skill_definitions?.name_he}
                      </span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          <div className="flex justify-end gap-2 pt-1">
            <button onClick={() => setShowForm(false)} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800">ביטול</button>
            <button onClick={handleCreate} disabled={creating || !newName.trim() || selectedSkillIds.length === 0}
              className="px-5 py-2 bg-blue-600 text-white text-sm font-medium rounded-xl hover:bg-blue-700 disabled:opacity-50 transition-colors flex items-center gap-2">
              {creating && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
              שמור זרימה
            </button>
          </div>
        </div>
      )}

      {/* Workflow list */}
      {workflows.length === 0 && !showForm && (
        <div className="text-center py-16 text-gray-400">
          <ArrowDown className="w-10 h-10 mx-auto mb-3 opacity-20" />
          <p className="text-sm">אין זרימות עבודה עדיין. צור את הראשונה שלך.</p>
        </div>
      )}

      <div className="space-y-3">
        {workflows.map(wf => {
          const isExpanded = expandedId === wf.id;
          const steps = (wf.workflow_steps ?? []).sort((a, b) => a.step_order - b.step_order);
          return (
            <div key={wf.id} className="bg-white border border-gray-200 rounded-2xl shadow-sm overflow-hidden">
              <div className="px-5 py-4 flex items-center gap-3">
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-gray-900">{wf.name}</p>
                  <p className="text-xs text-gray-500 mt-0.5">
                    {steps.length} סקיל{steps.length !== 1 ? 'ים' : ''} · {SCHEDULE_LABELS[wf.schedule_type as ScheduleType]}
                  </p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <button onClick={() => setExpandedId(isExpanded ? null : wf.id)}
                    className="p-1.5 text-gray-400 hover:text-gray-600 transition-colors">
                    {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                  </button>
                  <button onClick={() => onRunWorkflow(wf.id)} disabled={runningId === wf.id}
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-600 text-white text-xs font-medium rounded-lg hover:bg-emerald-700 disabled:opacity-50 transition-colors">
                    {runningId === wf.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Play className="w-3 h-3" />}
                    הפעל
                  </button>
                  <button onClick={() => handleDelete(wf.id)} className="p-1.5 text-gray-300 hover:text-red-500 transition-colors">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>

              {isExpanded && steps.length > 0 && (
                <div className="border-t border-gray-100 px-5 py-4">
                  <div className="flex flex-col gap-1">
                    {steps.map((s, i) => (
                      <div key={s.id} className="flex items-center gap-3">
                        <div className="flex flex-col items-center">
                          <div className="w-6 h-6 rounded-full bg-blue-100 text-blue-700 text-xs font-bold flex items-center justify-center">
                            {i + 1}
                          </div>
                          {i < steps.length - 1 && <div className="w-px h-4 bg-blue-200 mt-1" />}
                        </div>
                        <span className="text-sm text-gray-700">
                          {s.company_skills?.name_override ?? s.company_skills?.skill_definitions?.name_he ?? 'סקיל'}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
